import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, UtensilsCrossed, Dumbbell, TrendingUp, Camera,
  Sparkles, Loader2, AlertTriangle, CheckCircle2, Target,
  ArrowUp, ArrowDown, Minus, FileText, X, Bell, Trash2, Download, Euro, Copy, Link2,
  Activity, Award,
} from "lucide-react";
import CoachStudentBadges from "@/components/badges/CoachStudentBadges";
import { exportStudentDataPDF } from "@/lib/pdf-export";
import PhotoEvolutionPanel from "@/components/photos/PhotoEvolutionPanel";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import SportRefsPanel, { sportRefsFromApi } from "@/components/training/SportRefsPanel";
import { getDiscipline } from "@/lib/trainingDisciplines";

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [dietPlans, setDietPlans] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [progress, setProgress] = useState([]);
  const [photos, setPhotos] = useState([]);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);

  // AI Summary state
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  // AI Weekly Report state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // AI Notifications state
  const [notifyLoading, setNotifyLoading] = useState(false);

  // Photo request state
  const [photoRequestLoading, setPhotoRequestLoading] = useState(false);
  const [hasPhotoRequest, setHasPhotoRequest] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [trainingAdherence, setTrainingAdherence] = useState(null);
  const [adherenceLoading, setAdherenceLoading] = useState(true);
  const [unifiedAdherence, setUnifiedAdherence] = useState(null);
  const [unifiedLoading, setUnifiedLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sportRefs, setSportRefs] = useState({});

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [coachPackages, setCoachPackages] = useState([]);
  const [paySending, setPaySending] = useState(false);
  const [lastPayUrl, setLastPayUrl] = useState(null);
  const [payForm, setPayForm] = useState({ label: "", amount: "", description: "", package_id: "" });

  const isPro = user?.subscription?.plan_type === 'pro';

  useEffect(() => {
    api.get(`/students/${id}`).then(r => setStudent(r.data)).catch(() => navigate('/coach/students'));
    api.get('/diet-plans').then(r => setDietPlans(r.data.filter(p => p.student_id === id)));
    api.get('/training-plans').then(r => setTrainingPlans(r.data.filter(p => p.student_id === id)));
    api.get(`/progress?student_id=${id}`).then(r => setProgress(r.data)).catch(() => {});
    api.get(`/photos?student_id=${id}`).then(r => setPhotos(r.data)).catch(() => {});
    api.get('/inactivity/alerts').then(r => {
      const alert = (r.data?.alerts || []).find(a => a.student.user_id === id);
      if (alert?.has_pending_request) setHasPhotoRequest(true);
    }).catch(() => {});
    api.get(`/checkins/coach/${id}`).then(r => setCheckins(r.data?.checkins || [])).catch(() => {});
    setAdherenceLoading(true);
    setUnifiedLoading(true);
    api.get(`/workouts/adherence/student/${id}?days=30`)
      .then((r) => setTrainingAdherence(r.data))
      .catch(() => setTrainingAdherence(null))
      .finally(() => setAdherenceLoading(false));
    api.get(`/students/${id}/adherence-score?days=30`)
      .then((r) => setUnifiedAdherence(r.data))
      .catch(() => setUnifiedAdherence(null))
      .finally(() => setUnifiedLoading(false));
    api.get("/sport-refs", { params: { student_id: id } })
      .then((r) => setSportRefs(sportRefsFromApi(r.data || [])))
      .catch(() => setSportRefs({}));
  }, [id, navigate]);

  const sportRefDiscipline = (() => {
    const ids = trainingPlans.map((p) => p.discipline || "strength");
    if (ids.includes("hybrid")) return "hybrid";
    return ids.find((d) => getDiscipline(d).usesSportRefs) || null;
  })();

  const openPayDialog = async () => {
    setLastPayUrl(null);
    setPayForm({ label: "", amount: "", description: "", package_id: "" });
    setPayDialogOpen(true);
    try {
      const { data } = await api.get("/coach-packages");
      setCoachPackages((data.packages || []).filter((p) => p.is_active));
    } catch {
      setCoachPackages([]);
    }
  };

  const handlePackageSelect = (packageId) => {
    const pkg = coachPackages.find((p) => p.package_id === packageId);
    setPayForm((f) => ({
      ...f,
      package_id: packageId,
      label: pkg?.name || f.label,
      amount: pkg ? String(pkg.amount) : f.amount,
      description: pkg?.description || f.description,
    }));
  };

  const handleSendPaymentRequest = async (e) => {
    e.preventDefault();
    if (!payForm.label.trim() || !payForm.amount) {
      toast.error("Libellé et montant requis");
      return;
    }
    setPaySending(true);
    try {
      const { data } = await api.post("/payments/requests", {
        student_id: id,
        label: payForm.label.trim(),
        amount: parseFloat(payForm.amount),
        description: payForm.description.trim() || undefined,
        package_id: payForm.package_id || undefined,
        origin_url: window.location.origin,
      });
      setLastPayUrl(data.public_url);
      toast.success("Demande envoyée — notification, email et lien public créés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setPaySending(false);
    }
  };

  const copyPayLink = async () => {
    if (!lastPayUrl) return;
    await navigator.clipboard.writeText(lastPayUrl);
    toast.success("Lien copié");
  };

  // ─── Feature 1: Analyse de progression IA ───
  const handleAnalyzeProgress = async () => {
    setAiAnalysisLoading(true);
    try {
      const res = await api.post('/ai/analyze-progress', { student_id: id });
      setAiAnalysis(res.data);
      toast.success("Analyse générée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse IA");
    } finally { setAiAnalysisLoading(false); }
  };

  // ─── Résumé IA du profil ───
  const handleSummarize = async () => {
    setAiSummaryLoading(true);
    try {
      const res = await api.post('/ai/summarize-student', { student_id: id });
      setAiSummary(res.data.summary);
      toast.success("Résumé généré !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de résumé IA");
    } finally { setAiSummaryLoading(false); }
  };

  // ─── Notifications IA ───
  const handleSmartNotify = async () => {
    setNotifyLoading(true);
    try {
      const res = await api.post('/ai/smart-notifications', { student_id: id });
      toast.success(`${res.data.created} notification${res.data.created > 1 ? 's' : ''} envoyée${res.data.created > 1 ? 's' : ''} à ${student.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi des notifications");
    } finally { setNotifyLoading(false); }
  };

  // ─── Demande de photos ───
  const handlePhotoRequest = async () => {
    setPhotoRequestLoading(true);
    try {
      await api.post('/inactivity/photo-request', { student_id: id });
      toast.success("Demande de photos envoyée !");
      setHasPhotoRequest(true);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Une demande est déjà en cours");
        setHasPhotoRequest(true);
      } else {
        toast.error(err.response?.data?.detail || "Erreur");
      }
    } finally { setPhotoRequestLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      exportStudentDataPDF({
        student: {
          ...student,
          goal_weight: student.goal_weight,
          goal_date: student.goal_date,
        },
        dietPlans,
        trainingPlans,
        progress,
        checkins,
        photos,
      });
      toast.success("Export PDF généré !");
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/students/${id}`);
      toast.success("Élève supprimé de votre liste");
      navigate('/coach/students');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  // ─── Feature 4: Bilan hebdomadaire IA ───
  const handleWeeklyReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.post('/ai/weekly-report', { student_id: id });
      setReportData(res.data);
      setShowReport(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération du bilan");
    } finally { setReportLoading(false); }
  };

  const TrendIcon = ({ trend }) => {
    if (trend === "hausse") return <ArrowUp className="w-4 h-4 text-red-500" />;
    if (trend === "baisse") return <ArrowDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const TrendBadge = ({ trend }) => {
    const colors = {
      hausse: "bg-red-50 text-red-700 border-red-200",
      baisse: "bg-green-50 text-green-700 border-green-200",
      stable: "bg-slate-50 text-slate-600 border-slate-200",
    };
    return (
      <Badge variant="outline" className={`gap-1 ${colors[trend] || colors.stable}`}>
        <TrendIcon trend={trend} /> {trend === "hausse" ? "En hausse" : trend === "baisse" ? "En baisse" : "Stable"}
      </Badge>
    );
  };

  const ScoreBadge = ({ score }) => {
    const colors = {
      "Excellent": "bg-green-50 text-green-700 border-green-200",
      "Bon": "bg-blue-50 text-blue-700 border-blue-200",
      "Moyen": "bg-amber-50 text-amber-700 border-amber-200",
      "À améliorer": "bg-red-50 text-red-700 border-red-200",
    };
    return <Badge variant="outline" className={colors[score] || "bg-slate-50 text-slate-600"}>{score}</Badge>;
  };

  if (!student) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="student-detail-page">
      <button onClick={() => navigate('/coach/students')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-students-btn">
        <ArrowLeft className="w-4 h-4" /> Retour aux élèves
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600">
            {student.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{student.name}</h1>
            <p className="text-slate-500">{student.email}</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap justify-end">
          <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openPayDialog}
                variant="outline"
                className="gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50 rounded-full"
                data-testid="payment-request-btn"
              >
                <Euro className="w-4 h-4" /> Demande de paiement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Demander un paiement à {student.name}</DialogTitle>
              </DialogHeader>
              {lastPayUrl ? (
                <div className="space-y-4">
                  <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-xl p-4">
                    Demande envoyée : notification in-app + email à l&apos;élève.
                  </p>
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 border text-xs break-all">
                    <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="flex-1 text-slate-600">{lastPayUrl}</span>
                    <Button type="button" size="icon" variant="ghost" onClick={copyPayLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button className="w-full rounded-full" onClick={() => setPayDialogOpen(false)}>Fermer</Button>
                </div>
              ) : (
                <form onSubmit={handleSendPaymentRequest} className="space-y-4">
                  {coachPackages.length > 0 && (
                    <div>
                      <Label>Forfait (optionnel)</Label>
                      <Select value={payForm.package_id || "none"} onValueChange={(v) => v === "none" ? setPayForm((f) => ({ ...f, package_id: "" })) : handlePackageSelect(v)}>
                        <SelectTrigger><SelectValue placeholder="Montant personnalisé" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Montant personnalisé</SelectItem>
                          {coachPackages.map((p) => (
                            <SelectItem key={p.package_id} value={p.package_id}>{p.name} — {p.amount}€</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="pay-label">Libellé</Label>
                    <Input id="pay-label" value={payForm.label} onChange={(e) => setPayForm({ ...payForm, label: e.target.value })} placeholder="Ex: Coaching mars 2026" required />
                  </div>
                  <div>
                    <Label htmlFor="pay-amount">Montant (€)</Label>
                    <Input id="pay-amount" type="number" step="0.01" min="0.5" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} required />
                  </div>
                  <div>
                    <Label htmlFor="pay-desc">Message (optionnel)</Label>
                    <Textarea id="pay-desc" value={payForm.description} onChange={(e) => setPayForm({ ...payForm, description: e.target.value })} rows={2} placeholder="Détails pour l'élève..." />
                  </div>
                  <Button type="submit" disabled={paySending} className="w-full rounded-full gap-2">
                    {paySending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Euro className="w-4 h-4" />}
                    {paySending ? "Envoi..." : "Envoyer la demande"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
            className="gap-2 text-slate-700 border-slate-200 hover:bg-slate-50 rounded-full"
            data-testid="export-student-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? "Export..." : "Exporter PDF"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                disabled={deleting}
                data-testid="delete-student-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {student.name} ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cet élève sera retiré de votre liste. Son compte sera conservé mais il ne sera plus associé à votre coaching.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            onClick={handleSummarize}
            disabled={aiSummaryLoading}
            variant="outline"
            className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 rounded-full"
          >
            {aiSummaryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {aiSummaryLoading ? "Résumé..." : "Résumer"}
          </Button>
          <Button
            onClick={handleSmartNotify}
            disabled={notifyLoading}
            variant="outline"
            className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50 rounded-full"
          >
            {notifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            {notifyLoading ? "Envoi..." : "Notifier IA"}
          </Button>
          <Button
            onClick={handlePhotoRequest}
            disabled={photoRequestLoading || hasPhotoRequest}
            variant="outline"
            className="gap-2 text-purple-600 border-purple-200 hover:bg-purple-50 rounded-full"
          >
            {photoRequestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {hasPhotoRequest ? "Demande en cours" : photoRequestLoading ? "Envoi..." : "Demander photos"}
          </Button>
          <Button
            onClick={() => navigate(`/coach/students/${id}/report`)}
            variant="outline"
            className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 rounded-full"
          >
            <FileText className="w-4 h-4" /> Rapport PDF
          </Button>
          {isPro && (
            <Button
              onClick={handleWeeklyReport}
              disabled={reportLoading}
              className="gap-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full"
            >
              {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {reportLoading ? "Génération..." : "Bilan IA"}
            </Button>
          )}
        </div>
      </div>

      {/* Score d'adhérence unifié */}
      <Card className="mb-6 border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-blue-50/40" data-testid="unified-adherence-card">
        <CardContent className="p-5">
          {unifiedLoading ? (
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
              <span className="text-sm text-slate-500">Calcul du score d'adhérence...</span>
            </div>
          ) : !unifiedAdherence ? (
            <p className="text-sm text-slate-400">Score d'adhérence indisponible</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex items-center gap-4 shrink-0">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.5" fill="none"
                      stroke={unifiedAdherence.overall_score >= 70 ? "#22c55e" : unifiedAdherence.overall_score >= 40 ? "#f59e0b" : "#ef4444"}
                      strokeWidth="3"
                      strokeDasharray={`${unifiedAdherence.overall_score} 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-slate-900">{unifiedAdherence.overall_score}</span>
                    <span className="text-[10px] text-slate-400 uppercase">/100</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-indigo-600 uppercase tracking-widest font-semibold">Adhérence globale</p>
                  <p className="text-sm text-slate-600 mt-0.5">30 derniers jours</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Training 35% · Nutrition 30% · Check-in 20% · Compléments 15%
                  </p>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {Object.values(unifiedAdherence.components || {}).map((c) => (
                  <div key={c.key} className="p-3 rounded-xl bg-white/70 border border-slate-100">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-slate-600">{c.label}</span>
                      <span className={`text-sm font-bold ${c.available ? "text-slate-900" : "text-slate-300"}`}>
                        {c.available ? `${c.score}%` : "—"}
                      </span>
                    </div>
                    {c.available ? (
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            c.score >= 70 ? "bg-green-500" : c.score >= 40 ? "bg-amber-500" : "bg-red-400"
                          }`}
                          style={{ width: `${c.score}%` }}
                        />
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400 truncate">{c.detail}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résumé IA */}
      {aiSummary && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700 leading-relaxed">{aiSummary}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => setAiSummary(null)}>
                <X className="w-4 h-4 text-slate-400" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="diet" className="mt-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="diet" data-testid="tab-diet"><UtensilsCrossed className="w-4 h-4 mr-1.5" /> Diet ({dietPlans.length})</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training"><Dumbbell className="w-4 h-4 mr-1.5" /> Training ({trainingPlans.length})</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress"><TrendingUp className="w-4 h-4 mr-1.5" /> Progression</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos"><Camera className="w-4 h-4 mr-1.5" /> Photos ({photos.length})</TabsTrigger>
          <TabsTrigger value="adherence" data-testid="tab-adherence"><Activity className="w-4 h-4 mr-1.5" /> Adhérence</TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges"><Award className="w-4 h-4 mr-1.5" /> Badges</TabsTrigger>
        </TabsList>

        <TabsContent value="diet" className="mt-6">
          {dietPlans.length === 0 ? <p className="text-slate-400 text-center py-8">Aucun plan diététique assigné</p> : (
            <div className="space-y-3">
              {dietPlans.map(p => (
                <Card key={p.plan_id} className="border-slate-100">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-900">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {p.calories_target && <Badge variant="secondary">{p.calories_target} kcal</Badge>}
                      {p.protein_target && <Badge variant="secondary">{p.protein_target}g prot</Badge>}
                      {p.water_target && <Badge variant="secondary">{p.water_target} L eau</Badge>}
                      {p.steps_target && <Badge variant="secondary">{p.steps_target.toLocaleString("fr-BE")} pas</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="training" className="mt-6">
          {sportRefDiscipline && (
            <Card className="border-slate-100 mb-4">
              <CardContent className="p-5">
                <SportRefsPanel
                  studentId={id}
                  discipline={sportRefDiscipline}
                  values={sportRefs}
                  onChange={setSportRefs}
                  onSaved={(data) => setSportRefs(sportRefsFromApi(data || []))}
                  compact
                />
              </CardContent>
            </Card>
          )}
          {trainingPlans.length === 0 ? <p className="text-slate-400 text-center py-8">Aucun plan d'entraînement assigné</p> : (
            <div className="space-y-3">
              {trainingPlans.map(p => {
                const d = getDiscipline(p.discipline || "strength");
                const Icon = d.icon;
                return (
                <Card key={p.plan_id} className={`border-slate-100 border-l-4 ${d.accentBorder}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${d.light}`}>
                        <Icon className={`w-5 h-5 ${d.accent}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900">{p.title}</h3>
                          <DisciplineBadge discipline={p.discipline || "strength"} size="sm" />
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                        <p className="text-xs text-slate-400 mt-2">{p.days?.length || 0} jours · {d.logLabel}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="progress" className="mt-6">
          {/* Bouton Analyser avec IA */}
          {isPro && progress.length > 0 && (
            <div className="mb-4">
              <Button
                onClick={handleAnalyzeProgress}
                disabled={aiAnalysisLoading}
                variant="outline"
                className="gap-2 text-purple-600 border-purple-200 hover:bg-purple-50 rounded-full"
              >
                {aiAnalysisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {aiAnalysisLoading ? "Analyse en cours..." : "Analyser avec IA"}
              </Button>
            </div>
          )}

          {/* Résultat de l'analyse IA */}
          {aiAnalysis && (
            <Card className="mb-6 border-purple-200 bg-gradient-to-r from-purple-50/50 to-indigo-50/30">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Analyse IA</h3>
                    <Badge className="bg-purple-100 text-purple-700 text-[10px]">PRO</Badge>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAiAnalysis(null)}>
                    <X className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>

                {/* Résumé */}
                <p className="text-sm text-slate-700 leading-relaxed">{aiAnalysis.summary}</p>

                {/* Tendance poids */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-600">Tendance poids :</span>
                  <TrendBadge trend={aiAnalysis.weight_trend} />
                </div>

                {/* Analyse poids */}
                {aiAnalysis.weight_analysis && (
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">📊 Poids</p>
                    <p className="text-sm text-slate-700">{aiAnalysis.weight_analysis}</p>
                  </div>
                )}

                {/* Analyse mensurations */}
                {aiAnalysis.measurements_analysis && (
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1">📐 Mensurations</p>
                    <p className="text-sm text-slate-700">{aiAnalysis.measurements_analysis}</p>
                  </div>
                )}

                {/* Objectif */}
                {aiAnalysis.goal_progress && (
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><Target className="w-3 h-3" /> Objectif</p>
                    <p className="text-sm text-slate-700">{aiAnalysis.goal_progress}</p>
                  </div>
                )}

                {/* Alertes */}
                {aiAnalysis.alerts?.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <p className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alertes</p>
                    <ul className="space-y-1">
                      {aiAnalysis.alerts.map((a, i) => (
                        <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" /> {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommandations */}
                {aiAnalysis.recommendations?.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-xs font-semibold text-green-600 mb-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Recommandations</p>
                    <ul className="space-y-1">
                      {aiAnalysis.recommendations.map((r, i) => (
                        <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {progress.length === 0 ? <p className="text-slate-400 text-center py-8">Aucune donnée de progression</p> : (
            <div className="space-y-3">
              {progress.map(p => (
                <Card key={p.log_id} className="border-slate-100">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{p.date}</p>
                      {p.notes && <p className="text-xs text-slate-500 mt-1">{p.notes}</p>}
                    </div>
                    <div className="text-right">
                      {p.weight && <Badge variant="secondary">{p.weight} kg</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <PhotoEvolutionPanel
            photos={photos}
            emptyMessage="Aucune photo partagée par cet élève"
          />
        </TabsContent>

        <TabsContent value="adherence" className="mt-6" data-testid="tab-adherence-content">
          {adherenceLoading ? (
            <p className="text-slate-400 text-sm text-center py-8">Chargement...</p>
          ) : !trainingAdherence ? (
            <p className="text-slate-400 text-center py-8">Données indisponibles</p>
          ) : (
            <div className="space-y-4">
              <Card className="border-l-4 border-l-orange-500 border-slate-100">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Adhérence entraînement (30 jours)</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {trainingAdherence.has_plan ? `${trainingAdherence.adherence_percent}%` : "—"}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        {trainingAdherence.has_plan
                          ? `${trainingAdherence.sessions_logged} séances loguées sur ${trainingAdherence.sessions_expected} attendues`
                          : "Aucun programme assigné"}
                      </p>
                      {trainingAdherence.plan_title && (
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <p className="text-xs text-slate-400">{trainingAdherence.plan_title} · {trainingAdherence.sessions_per_week} séance(s)/sem.</p>
                          {trainingAdherence.plan_discipline && (
                            <DisciplineBadge discipline={trainingAdherence.plan_discipline} size="sm" />
                          )}
                        </div>
                      )}
                      {trainingAdherence.days_since_last != null && (
                        <p className={`text-xs mt-2 ${trainingAdherence.days_since_last > 7 ? "text-amber-600" : "text-slate-500"}`}>
                          Dernière séance : {trainingAdherence.last_session_date || "—"}
                          {trainingAdherence.days_since_last > 0 && ` (il y a ${trainingAdherence.days_since_last} jour${trainingAdherence.days_since_last > 1 ? "s" : ""})`}
                        </p>
                      )}
                    </div>
                    {trainingAdherence.has_plan && (
                      <div className="w-36">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              trainingAdherence.adherence_percent >= 70
                                ? "bg-green-500"
                                : trainingAdherence.adherence_percent >= 40
                                  ? "bg-amber-500"
                                  : "bg-red-400"
                            }`}
                            style={{ width: `${trainingAdherence.adherence_percent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {(trainingAdherence.history || []).length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-slate-700">Historique des séances</h3>
                  {trainingAdherence.history.map((s) => (
                    <Card key={s.session_id} className="border-slate-100">
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{s.day_name}</p>
                          <p className="text-xs text-slate-500">{s.session_date}</p>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          {s.total_volume_kg != null && <p>{Math.round(s.total_volume_kg).toLocaleString("fr-FR")} kg vol.</p>}
                          {s.total_distance_m != null && <p>{(s.total_distance_m / 1000).toFixed(1)} km</p>}
                          {s.duration_min != null && <p>{s.duration_min} min</p>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-6 text-sm">Aucune séance loguée sur les 30 derniers jours</p>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="badges" className="mt-6" data-testid="tab-badges-content">
          <CoachStudentBadges studentId={id} />
        </TabsContent>
      </Tabs>

      {/* ─── Dialog Bilan Hebdomadaire IA ─── */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <DialogTitle className="font-heading text-xl">{reportData?.title || "Bilan IA"}</DialogTitle>
              <Badge className="bg-purple-100 text-purple-700 text-[10px]">PRO</Badge>
            </div>
            {reportData?.date_range && (
              <p className="text-sm text-slate-500 mt-1">{reportData.date_range}</p>
            )}
          </DialogHeader>

          {reportData && (
            <div className="space-y-4 mt-2">
              {/* Résumé */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-slate-700 leading-relaxed">{reportData.summary}</p>
              </div>

              {/* Section Poids */}
              {reportData.weight_section && (
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">⚖️ Poids</h4>
                    <TrendBadge trend={reportData.weight_section.trend} />
                  </div>
                  <div className="flex items-baseline gap-3 mb-2">
                    {reportData.weight_section.current && (
                      <span className="text-2xl font-bold text-slate-900">{reportData.weight_section.current} kg</span>
                    )}
                    {reportData.weight_section.change !== undefined && reportData.weight_section.change !== null && (
                      <span className={`text-sm font-medium ${reportData.weight_section.change < 0 ? 'text-green-600' : reportData.weight_section.change > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        {reportData.weight_section.change > 0 ? '+' : ''}{reportData.weight_section.change} kg
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{reportData.weight_section.comment}</p>
                </div>
              )}

              {/* Section Mensurations */}
              {reportData.measurements_section && (
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">📐 Mensurations</h4>
                  {reportData.measurements_section.highlights?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {reportData.measurements_section.highlights.map((h, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{h}</Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-slate-600">{reportData.measurements_section.comment}</p>
                </div>
              )}

              {/* Adhérence Diet & Training */}
              <div className="grid grid-cols-2 gap-3">
                {reportData.diet_adherence && (
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-slate-900">🥗 Diet</h4>
                      <ScoreBadge score={reportData.diet_adherence.score} />
                    </div>
                    <p className="text-xs text-slate-600">{reportData.diet_adherence.comment}</p>
                  </div>
                )}
                {reportData.training_adherence && (
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-slate-900">💪 Training</h4>
                      <ScoreBadge score={reportData.training_adherence.score} />
                    </div>
                    <p className="text-xs text-slate-600">{reportData.training_adherence.comment}</p>
                  </div>
                )}
              </div>

              {/* Points forts */}
              {reportData.points_forts?.length > 0 && (
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Points forts
                  </h4>
                  <ul className="space-y-1.5">
                    {reportData.points_forts.map((p, i) => (
                      <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Points d'amélioration */}
              {reportData.points_amelioration?.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                  <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Points d'amélioration
                  </h4>
                  <ul className="space-y-1.5">
                    {reportData.points_amelioration.map((p, i) => (
                      <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Objectifs semaine prochaine */}
              {reportData.objectifs_semaine?.length > 0 && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1.5">
                    <Target className="w-4 h-4" /> Objectifs semaine prochaine
                  </h4>
                  <ul className="space-y-1.5">
                    {reportData.objectifs_semaine.map((o, i) => (
                      <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" /> {o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
